// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { LinkifiedText } from '@/components/linkified-text';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { PROJECT_DESCRIPTION_MAX_LENGTH, Project } from '@/project/api/types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const EMPTY_STATE = 'Project description not provided';

const formSchema = z.object({ description: z.string().nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  project: Pick<Project, 'key' | 'description'>;
}

export function ProjectDescriptionControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const [expanded, setExpanded] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);

  const { formState, register, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { description: props.project.description ?? '' },
  });

  const updateProject = useUpdateProject(props.project.key, {
    onSuccess: (project) => {
      reset({ description: project.description ?? '' });
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const { isDirty } = formState;

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isDirty) {
      const description = data.description === '' ? null : data.description;

      return updateProject.mutate({ description });
    }

    editor.close();
  };

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const description = props.project.description ?? EMPTY_STATE;
  const color =
    props.project.description === null ? 'text.secondary' : 'text.primary';

  const isLongDescription =
    description.length > 200 || description.split('\n').length > 4;

  return (
    <>
      <EditablePropertyButton
        ref={buttonRef}
        component="div"
        onClick={editor.open}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: '5rem',
          height: '100%',
          alignItems: 'start',
          justifyContent: 'start',
          whiteSpace: 'pre-wrap',
          textAlign: 'left',
          borderLeft: '2px solid',
          borderColor: 'divider',
          borderRadius: 0,
          position: 'relative',
          '& .edit-icon': {
            position: 'absolute',
            top: '0.4rem',
            right: '0.3rem',
            margin: 0,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            position: 'relative',
          }}
        >
          <Typography
            variant="body2"
            textAlign="left"
            marginRight="auto"
            whiteSpace="pre-wrap"
            color={color}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 4,
              WebkitBoxOrient: 'vertical',
              overflow: expanded ? 'auto' : 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: expanded ? '20rem' : 'unset',
              overflowY: expanded ? 'auto' : 'hidden',
            }}
          >
            <LinkifiedText>{description}</LinkifiedText>
          </Typography>
          {isLongDescription && (
            <Typography
              component="span"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
              sx={{
                fontStyle: 'italic',
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {expanded ? 'read less' : 'read more'}
            </Typography>
          )}
        </Box>
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateProject.isError}
        onClose={updateProject.reset}
        message="An error occurred while updating the project."
      />
      <PopoverForm
        verticalOffset={24}
        horizontalOffset={8}
        position="left"
        verticalPosition="top"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          multiline
          rows={3}
          size="small"
          InputProps={register('description')}
          inputProps={{ maxLength: PROJECT_DESCRIPTION_MAX_LENGTH }}
          sx={{ width: '20rem' }}
        />
      </PopoverForm>
    </>
  );
}
